/**
 * Free Gift Handler
 * Automatically adds/removes free gift product based on customer eligibility
 */
class FreeGiftHandler {
  constructor(config) {
    this.freeGiftProductId = config.freeGiftProductId;
    this.freeGiftProductHandle = config.freeGiftProductHandle || null;
    this.freeGiftVariantId = config.freeGiftVariantId || null;
    this.isCustomerLoggedIn = config.isCustomerLoggedIn;
    this.isFirstTimeCustomer = config.isFirstTimeCustomer;
    this.isProcessing = false;
    this.cartUpdateInProgress = false;
    this.init();
  }

  init() {
    // Listen for cart updates using pubsub system
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'free-gift') {
          return; // Don't process our own updates
        }
        this.handleCartUpdate(event);
      });
    } else {
      // Fallback: use custom event
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.source === 'free-gift') {
          return;
        }
        this.handleCartUpdate(event);
      });
    }

    // Intercept checkout when cart has only free gift (remove free gift first, then go to checkout)
    document.addEventListener('click', this.handleCheckoutClick.bind(this), true);

    // Common: disable checkout button when cart is loading (cart__items--disabled) or when free-gift is processing
    this.setupCheckoutButtonDisable();

    // Prefetch variant so the add is instant, then check right away on load
    if (this.isEligible()) this.getFreeGiftVariantId();
    this.checkAndManageFreeGift();
  }

  /**
   * Returns the cart items container(s) that get the loading class (cart__items--disabled).
   * Theme uses this when updating quantity or removing an item.
   */
  getCartItemsContainers() {
    const ids = ['CartDrawer-CartItems', 'main-cart-items'];
    return ids.map((id) => document.getElementById(id)).filter(Boolean);
  }

  /**
   * True if any cart items container has the loading/disabled class (add/remove/update in progress).
   */
  isCartItemsLoading() {
    const containers = this.getCartItemsContainers();
    const loadingClass = 'cart__items--disabled';
    return containers.some((el) => el.classList.contains(loadingClass));
  }

  /**
   * Returns all checkout buttons and links in cart context (drawer + main cart page).
   */
  getCheckoutButtons() {
    const selector =
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], #CartDrawer-Checkout';
    return document.querySelectorAll(selector);
  }

  /**
   * Disable checkout button when cart is loading or free-gift is processing; otherwise enable.
   * Common logic: prevents checkout with stale cart (e.g. only free gift after removing last paid product).
   */
  updateCheckoutButtonState() {
    const shouldDisable = this.isCartItemsLoading() || this.isProcessing;
    this.getCheckoutButtons().forEach((el) => {
      if (el.tagName === 'A') {
        el.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
        el.style.pointerEvents = shouldDisable ? 'none' : '';
        el.style.opacity = shouldDisable ? '0.6' : '';
        el.classList.toggle('cart__checkout-button--disabled', shouldDisable);
      } else {
        el.disabled = shouldDisable;
        el.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
        el.classList.toggle('cart__checkout-button--disabled', shouldDisable);
      }
    });
  }

  /**
   * Watch for cart items container class changes (loading state) and keep checkout button in sync.
   */
  setupCheckoutButtonDisable() {
    this.updateCheckoutButtonState();

    const observer = new MutationObserver(() => {
      this.updateCheckoutButtonState();
    });

    const observeContainers = () => {
      this.getCartItemsContainers().forEach((el) => {
        if (el && !el.dataset.checkoutObserver) {
          el.dataset.checkoutObserver = '1';
          observer.observe(el, { attributes: true, attributeFilter: ['class'] });
        }
      });
    };

    observeContainers();

    // Re-observe after cart drawer/html updates (e.g. section re-render replaces nodes)
    this.reobserveCheckoutObserver = () => {
      this.getCartItemsContainers().forEach((el) => {
        if (el && el.dataset.checkoutObserver) return;
        observeContainers();
      });
    };
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.reobserveCheckoutObserver.bind(this));
    }
    document.addEventListener('cart:updated', this.reobserveCheckoutObserver.bind(this));
  }

  /**
   * Intercept checkout: prevent reaching checkout with only the free gift (blocks
   * unlimited free orders). Must preventDefault synchronously before any await.
   * Also ignore click when cart is loading or free-gift is processing (checkout button is disabled).
   */
  handleCheckoutClick(event) {
    const target = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
      : null;
    if (!target) return;

    const form = target.form || (target.getAttribute('form') && document.getElementById(target.getAttribute('form')));
    const isCartCheckout =
      (form && (form.id === 'CartDrawer-Form' || form.id === 'cart')) ||
      (target.tagName === 'A' && target.getAttribute('href') && target.getAttribute('href').includes('/checkout'));

    if (!isCartCheckout) return;

    // Block navigation immediately so user cannot reach checkout with only free gift
    event.preventDefault();
    event.stopPropagation();

    // Don't proceed if checkout is disabled (cart loading or free-gift processing)
    if (this.isCartItemsLoading() || this.isProcessing) return;

    this.runCheckoutGuardAsync();
  }

  /**
   * Async guard: if cart has only free gift, remove it and stay on cart; otherwise allow checkout.
   */
  async runCheckoutGuardAsync() {
    const cart = await this.getCart();
    if (!cart) {
      this.redirectToCheckout();
      return;
    }
    const hasFreeGift = this.hasFreeGiftInCart(cart);
    const hasOtherProducts = this.hasOtherProductsInCart(cart);

    if (hasOtherProducts) {
      // Valid cart: allow checkout
      this.redirectToCheckout();
      return;
    }

    if (hasFreeGift) {
      // Cart has only free gift: remove it and do NOT go to checkout (blocks $0 / unlimited free orders)
      await this.removeFreeGift();
      return;
    }

    // Empty cart: allow checkout (will show empty cart there)
    this.redirectToCheckout();
  }

  redirectToCheckout() {
    const checkoutUrl = typeof routes !== 'undefined' && routes.checkout_url ? routes.checkout_url : '/checkout';
    window.location.href = checkoutUrl;
  }

  async handleCartUpdate(event) {
    // Debounce to avoid multiple rapid calls
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(async () => {
      await this.checkAndManageFreeGift();

      // After managing free gift, check if cart is empty and refresh drawer if needed
      const cart = await this.getCart();
      if (!cart || cart.item_count === 0) {
        // Cart is empty, force a full cart drawer refresh
        await this.updateCartDrawerHTML();
      }
      if (this.reobserveCheckoutObserver) this.reobserveCheckoutObserver();
      this.updateCheckoutButtonState();
    }, 300);
  }

  async checkAndManageFreeGift() {
    if (this.isProcessing) {
      // A cart update arrived mid-operation; re-check once the current one finishes
      this.pendingRecheck = true;
      return;
    }

    // Check eligibility
    if (!this.isEligible()) {
      // Remove free gift if customer is not eligible
      await this.removeFreeGift();
      return;
    }

    // Get current cart
    const cart = await this.getCart();
    if (!cart) return;

    // Check if free gift is already in cart
    const hasFreeGift = this.hasFreeGiftInCart(cart);
    const hasOtherProducts = this.hasOtherProductsInCart(cart);

    if (hasOtherProducts && !hasFreeGift) {
      // Add free gift
      await this.addFreeGift();
    } else if (!hasOtherProducts && hasFreeGift) {
      // Remove free gift if no other products
      await this.removeFreeGift();
    }
  }

  isEligible() {
    // Must be logged in and first-time customer
    return this.isCustomerLoggedIn && this.isFirstTimeCustomer;
  }

  async getCart() {
    try {
      const cartUrl = typeof routes !== 'undefined' && routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';
      const response = await fetch(cartUrl);
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  hasFreeGiftInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some((item) => item.product_id.toString() === this.freeGiftProductId.toString());
  }

  hasOtherProductsInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some((item) => item.product_id.toString() !== this.freeGiftProductId.toString());
  }

  async getFreeGiftVariantId() {
    // If variant ID is explicitly provided from Liquid, use it (most reliable)
    if (this.freeGiftVariantId) {
      return this.freeGiftVariantId;
    }
    if (!this.variantIdPromise) {
      this.variantIdPromise = this.fetchFreeGiftVariantId().then((id) => {
        if (!id) this.variantIdPromise = null; // allow retry on failure
        return id;
      });
    }
    return this.variantIdPromise;
  }

  async fetchFreeGiftVariantId() {
    // Try to fetch the product via API - use handle if available, otherwise use ID
    try {
      // Prefer product handle over ID (more reliable for published products)
      const productIdentifier = this.freeGiftProductHandle || this.freeGiftProductId;
      const productUrl = `/products/${productIdentifier}.js`;
      console.log('Fetching product from API:', productUrl);
      const response = await fetch(productUrl);

      if (response.ok) {
        const product = await response.json();
        console.log('Product fetched successfully:', {
          id: product.id,
          title: product.title,
          variantsCount: product.variants?.length,
        });

        // Only use a variant that can actually be purchased
        const variant = product.variants?.find((v) => v.available);
        if (variant?.id) {
          return variant.id.toString();
        }
        console.error('Free gift has no available (in-stock) variant - skipping');
        return null;
      } else {
        const errorText = await response.text();
        console.error('Product fetch failed:', response.status, errorText);
        console.error('Product might be unpublished or does not exist on storefront');
        return null;
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  async requestAddFreeGift(variantId) {
    const addUrl = typeof routes !== 'undefined' && routes.cart_add_url ? routes.cart_add_url : '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: variantId.toString(),
        quantity: 1,
        properties: { _free_gift: 'true' },
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText };
      }
      throw new Error(errorData.description || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.status) {
      throw new Error(result.description || result.errors || 'Failed to add free gift');
    }
    return result;
  }

  /**
   * Run the eligibility/add/remove check again if a cart update arrived while busy.
   */
  flushPendingRecheck() {
    if (!this.pendingRecheck) return;
    this.pendingRecheck = false;
    setTimeout(() => this.checkAndManageFreeGift(), 0);
  }

  async addFreeGift() {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.updateCheckoutButtonState();

    try {
      const variantId = await this.getFreeGiftVariantId();
      if (!variantId) {
        console.error('Free gift variant not found - cannot add to cart.');
        this.isProcessing = false;
        return;
      }

      // Product was found, so retry the add a couple of times if it fails.
      // (Not retried above when the product is missing/draft/sold out.)
      const maxAttempts = 3;
      let result = null;
      let lastError = null;
      for (let attempt = 1; attempt <= maxAttempts && !result; attempt++) {
        try {
          result = await this.requestAddFreeGift(variantId);
        } catch (error) {
          lastError = error;
          console.error(`Free gift add failed (attempt ${attempt}/${maxAttempts}):`, error);
          if (attempt < maxAttempts) await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }
      if (!result) throw lastError;

      // Render drawer from the add response and open it immediately
      const cartDrawer = document.querySelector('cart-drawer');
      if (result.sections) {
        this.applyCartSectionsToDrawer(result);
      } else {
        await this.updateCartDrawerHTML();
      }
      if (cartDrawer) cartDrawer.classList.remove('is-empty');
      this.openCartDrawer();

      // Sync counts, Route widget and listeners in the background
      this.triggerCartUpdate(true);
    } catch (error) {
      console.error('Error adding free gift:', error);
    } finally {
      this.isProcessing = false;
      this.updateCheckoutButtonState();
      this.flushPendingRecheck();
    }
  }

  /**
   * Remove free gift using the same flow as cart-remove-button: change request with sections,
   * then update the cart drawer from the response. Keeps Route widget and drawer in sync.
   * When user removes the only paid product (cart = paid + free gift), handleCartUpdate
   * runs and calls this to clear the cart.
   */
  async removeFreeGift() {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.updateCheckoutButtonState();

    try {
      const cart = await this.getCart();
      if (!cart) {
        this.isProcessing = false;
        return;
      }

      const freeGiftItem = cart.items.find((item) => item.product_id.toString() === this.freeGiftProductId.toString());
      if (!freeGiftItem) {
        this.isProcessing = false;
        return;
      }

      const changeUrl =
        typeof routes !== 'undefined' && routes.cart_change_url ? routes.cart_change_url : '/cart/change.js';
      const body = {
        id: freeGiftItem.key,
        quantity: 0,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      };
      const fetchOpts =
        typeof fetchConfig !== 'undefined'
          ? { ...fetchConfig(), body: JSON.stringify(body) }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            };

      const response = await fetch(changeUrl, fetchOpts);
      const text = await response.text();
      let parsedState;
      try {
        parsedState = JSON.parse(text);
      } catch (e) {
        throw new Error('Failed to parse cart response');
      }
      if (parsedState.errors) {
        throw new Error(parsedState.errors || 'Failed to remove free gift');
      }

      // Update cart drawer from section HTML (same as theme's updateQuantity / cart-remove-button)
      this.applyCartSectionsToDrawer(parsedState);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
      }
      await this.updateCartCountBubbles(parsedState);
      if (this.reobserveCheckoutObserver) this.reobserveCheckoutObserver();
      this.updateCheckoutButtonState();
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: parsedState });
      }
    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isProcessing = false;
      this.updateCheckoutButtonState();
      this.flushPendingRecheck();
    }
  }

  /**
   * Apply section HTML from a cart API response to the cart drawer (same structure as theme's updateQuantity).
   */
  applyCartSectionsToDrawer(parsedState) {
    if (!parsedState.sections) return;
    const sectionsToRender = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
    const getSectionInnerHTML = (html, selector) => {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector(selector);
      return el ? el.innerHTML : html;
    };
    sectionsToRender.forEach((section) => {
      const sectionHtml = parsedState.sections[section.section];
      if (!sectionHtml) return;
      const target = document.getElementById(section.id);
      if (!target) return;
      const elementToReplace = target.querySelector(section.selector) || target;
      elementToReplace.innerHTML = getSectionInnerHTML(sectionHtml, section.selector);
    });
  }

  /**
   * Opens the cart drawer after a short delay so the drawer HTML is updated first.
   * When fromFreeGiftAdd is true, uses a longer delay so Route widget has time to reinitialize.
   */
  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer.classList.contains('active')) return;
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      cartDrawer.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
    }
  }

  /**
   * Reinitialize the Route Product Protection widget after cart drawer HTML is updated.
   * Route injects into .route-div asynchronously; when we replace the footer/cart drawer,
   * a new empty .route-div is inserted and Route does not re-scan. Dispatching cart
   * events and calling known Route APIs encourages the widget to reinitialize.
   */
  reinitializeRouteWidget() {
    const routeDiv = document.querySelector('.cart-drawer .route-div, .main-cart_drawer .route-div');
    if (!routeDiv) return;

    // Events some themes and apps (e.g. Route) use to refresh cart-dependent widgets
    const events = [
      new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'free-gift' } }),
      new CustomEvent('cart:refresh', { bubbles: true }),
      new CustomEvent('cart:build', { bubbles: true }),
      new CustomEvent('route:cart:updated', { bubbles: true }),
    ];
    events.forEach((ev) => document.dispatchEvent(ev));

    // Shopify section:load style event (some apps listen for this)
    if (typeof document.dispatchEvent === 'function') {
      try {
        document.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: 'cart-drawer' },
          })
        );
      } catch (e) {}
    }

    // Route app may expose a global refresh method
    try {
      if (typeof window.Route !== 'undefined' && typeof window.Route.refresh === 'function') {
        window.Route.refresh();
      }
      if (typeof window.route !== 'undefined' && typeof window.route.refresh === 'function') {
        window.route.refresh();
      }
    } catch (e) {}
  }

  async triggerCartUpdate(fromFreeGiftAdd = false) {
    // First, update the cart drawer HTML to reflect the changes (full refresh so Route can bind to new .route-div)
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      // Use the built-in cart drawer update method
      try {
        await cartDrawerItems.onCartUpdate();
      } catch (error) {
        console.error('Error in cart drawer onCartUpdate:', error);
        // Fallback to manual update
        await this.updateCartDrawerHTML();
      }
    } else {
      // Fallback: manually fetch and update cart drawer
      await this.updateCartDrawerHTML();
    }

    // Update cart count everywhere (header + drawer) so count stays in sync after add/remove free gift
    const cart = await this.getCart();
    await this.updateCartCountBubbles(cart);

    // Wait for DOM to update, then publish cart event and reinitialize Route widget
    setTimeout(() => {
        // Fallback: dispatch custom event
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
      } else {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'free-gift' },
          })
        );
      }
      if (this.reobserveCheckoutObserver) this.reobserveCheckoutObserver();
      this.updateCheckoutButtonState();

      // Reinitialize Route Product Protection widget so it injects into the new .route-div
      this.reinitializeRouteWidget();
      if (fromFreeGiftAdd) {
        setTimeout(() => this.reinitializeRouteWidget(), 250);
      }

      // Reinitialize recommendations after a delay (but only if cart is not empty)
      setTimeout(async () => {
        const cart = await this.getCart();
        if (cart && cart.item_count > 0) {
          if (typeof window.initRecommendations === 'function') {
            const container = document.querySelector('.cart-drawer-recommendations');
            if (container) {
              container.dataset.initialized = '';
              window.initRecommendations();
            }
          }
        }
      }, 200);
    }, 150);
  }

  async updateCartDrawerHTML() {
    try {
      // Fetch the updated cart drawer section
      const cartDrawerUrl =
        typeof routes !== 'undefined' && routes.cart_url
          ? `${routes.cart_url}?section_id=cart-drawer`
          : '/cart?section_id=cart-drawer';

      const response = await fetch(cartDrawerUrl);
      if (!response.ok) {
        console.error('Failed to fetch cart drawer:', response.status);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // The section might be wrapped in a .shopify-section div
      // Try to find the cart-drawer element or #CartDrawer
      let newCartDrawerElement = doc.querySelector('cart-drawer');
      let newCartDrawer = doc.querySelector('#CartDrawer');

      // If not found, try looking in .shopify-section
      if (!newCartDrawerElement || !newCartDrawer) {
        const section = doc.querySelector('.shopify-section');
        if (section) {
          newCartDrawerElement = section.querySelector('cart-drawer') || newCartDrawerElement;
          newCartDrawer = section.querySelector('#CartDrawer') || newCartDrawer;
        }
      }

      const currentCartDrawer = document.querySelector('#CartDrawer');
      const currentCartDrawerElement = document.querySelector('cart-drawer');

      if (newCartDrawer && currentCartDrawer) {
        // Update the entire cart drawer content including header (with count bubble),
        // empty state, items, footer, discount section, etc.
        // This ensures everything is properly updated when cart becomes empty
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      // Update the cart-drawer element's class
      if (newCartDrawerElement && currentCartDrawerElement) {
        currentCartDrawerElement.className = newCartDrawerElement.className;
      }

      // When cart is empty, the server-rendered drawer has no 'active' class, so copying
      // className above visually "closes" the drawer but never calls close() — so
      // body keeps overflow-hidden and scroll stays broken. Call close() to remove it.
      if (currentCartDrawerElement && currentCartDrawerElement.classList.contains('is-empty')) {
        if (typeof currentCartDrawerElement.close === 'function') {
          currentCartDrawerElement.close();
        } else {
          document.body.classList.remove('overflow-hidden');
        }
      }

      // If we successfully updated, return early
      if (newCartDrawer && currentCartDrawer) {
        // Reinitialize scripts that might have been lost
        setTimeout(async () => {
          // Reinitialize discount handler
          if (typeof checkExistingDiscount === 'function') {
            checkExistingDiscount();
          }
          this.reinitializeRouteWidget();
          // Reinitialize recommendations only if cart is not empty
          const cartData = await this.getCart();
          if (cartData && cartData.item_count > 0) {
            if (typeof window.initRecommendations === 'function') {
              const container = document.querySelector('.cart-drawer-recommendations');
              if (container) {
                container.dataset.initialized = '';
                window.initRecommendations();
              }
            }
          }
        }, 100);
        return;
      }

      // Fallback: update individual sections if main update didn't work
      const cartFallback = await this.getCart();
      const isEmptyFallback = !cartFallback || cartFallback.item_count === 0;

      const newCartDrawerItems = doc.querySelector('cart-drawer-items');
      const newFooter = doc.querySelector('.cart-drawer__footer');
      const newDiscount = doc.querySelector('.cart-drawer__discount');
      const newEmptyState = doc.querySelector('.drawer__inner-empty');

      // Update cart drawer items
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');
      if (newCartDrawerItems && currentCartDrawerItems) {
        currentCartDrawerItems.replaceWith(newCartDrawerItems);
      } else if (isEmptyFallback && currentCartDrawerItems) {
        // If cart is empty and no new items, remove the items element
        currentCartDrawerItems.remove();
      }

      // Update footer
      const currentFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) {
        currentFooter.replaceWith(newFooter);
      }

      // Update discount section
      const currentDiscount = document.querySelector('.cart-drawer__discount');
      if (newDiscount && currentDiscount) {
        currentDiscount.replaceWith(newDiscount);
      } else if (isEmptyFallback && currentDiscount) {
        // Hide discount section if cart is empty
        currentDiscount.style.display = 'none';
      }

      // Handle empty state
      const drawerInner = document.querySelector('.drawer__inner');
      if (isEmptyFallback) {
        // Show empty state
        if (newEmptyState) {
          const currentEmptyState = drawerInner?.querySelector('.drawer__inner-empty');
          if (currentEmptyState) {
            currentEmptyState.replaceWith(newEmptyState);
          } else if (drawerInner) {
            drawerInner.appendChild(newEmptyState);
          }
        }
      } else {
        // Remove empty state if cart has items
        const currentEmptyState = drawerInner?.querySelector('.drawer__inner-empty');
        if (currentEmptyState) {
          currentEmptyState.remove();
        }
      }

      // Reinitialize scripts that might have been lost (fallback path)
      setTimeout(async () => {
        // Reinitialize discount handler
        if (typeof checkExistingDiscount === 'function') {
          checkExistingDiscount();
        }
        this.reinitializeRouteWidget();
        // Reinitialize recommendations only if cart is not empty
        const cart = await this.getCart();
        if (cart && cart.item_count > 0) {
          if (typeof window.initRecommendations === 'function') {
            const container = document.querySelector('.cart-drawer-recommendations');
            if (container) {
              container.dataset.initialized = '';
              window.initRecommendations();
            }
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error updating cart drawer HTML:', error);
    }
  }

  /**
   * Updates all cart count bubbles (header + drawer) so the count stays in sync
   * when free gift is added or removed via fetch (no full page reload).
   */
  async updateCartCountBubbles(cart) {
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    const displayCount = count >= 100 ? '99+' : String(count);

    // Update every existing cart-count-bubble on the page
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const visibleSpan = bubble.querySelector('span[aria-hidden="true"]');
      if (visibleSpan) {
        visibleSpan.textContent = displayCount;
      }
      bubble.style.display = count > 0 ? '' : 'none';
    });

    // If count > 0 but header has no bubble (e.g. cart was empty on load), fetch section and inject it
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (count > 0 && cartIconBubble && !cartIconBubble.querySelector('.cart-count-bubble')) {
      try {
        const sectionUrl =
          typeof routes !== 'undefined' && routes.cart_url
            ? `${routes.cart_url}?section_id=cart-icon-bubble`
            : '/cart?section_id=cart-icon-bubble';
        const response = await fetch(sectionUrl);
        if (response.ok) {
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section =
            doc.querySelector('#shopify-section-cart-icon-bubble') || doc.querySelector('.shopify-section');
          if (section) {
            cartIconBubble.innerHTML = section.innerHTML;
          }
        }
      } catch (e) {
        console.error('Error fetching cart icon bubble section:', e);
      }
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeGift);
} else {
  initFreeGift();
}

function initFreeGift() {
  // Get config from data attributes or window object
  const config = window.freeGiftConfig || {};

  if (config.freeGiftProductId) {
    window.freeGiftHandler = new FreeGiftHandler(config);
  }
}
